import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../i18n';
import Layout from '../components/Layout';
import Bare from '../components/Bare';
import { productionAPI } from '../api';
import toast from 'react-hot-toast';

// ─── Что выпустили ──────────────────────────────────────────────────────────
//
// По одному листу ответ был и раньше: бирка открывает его паспорт. Но вопрос
// приходит с другой стороны — «с белым гладким что-то не так», — и от декора
// надо спуститься к партиям, от партии к режиму и людям. Снизу вверх этот
// путь не проходится: полторы тысячи листов одним списком не читаются.
//
// Поэтому три уровня, каждый следующий уже предыдущего:
//   продукты → смены этого продукта → режим смены и её листы

const CARD = {
  background: 'var(--card)', border: '1px solid var(--border)',
  borderRadius: 14, padding: 18,
};

function dt(iso, withTime) {
  if (!iso) return '';
  const d = new Date(iso);
  const day = d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
  if (!withTime) return day;
  return day + ' ' + d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function hhmm(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

// Сорта: высший зелёным, остальное — по убыванию годности
function Grades({ items }) {
  if (!items || !items.length) return null;
  return (
    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
      {items.map((g, i) => (
        <span key={i} style={{
          background: g.is_default ? '#22c55e18' : 'var(--bg)',
          border: '1px solid ' + (g.is_default ? '#22c55e50' : 'var(--border)'),
          color: g.is_default ? '#22c55e' : 'var(--muted)',
          borderRadius: 20, padding: '2px 11px', fontSize: 12.5, fontWeight: 700,
        }}>{t(g.label)} {g.count}</span>
      ))}
    </div>
  );
}

export default function RegistryPage({ bare }) {
  const Shell = bare ? Bare : Layout;

  // Куда смотрим: список продуктов, смены одного продукта, одна смена
  const [nom, setNom]   = useState(null);   // { id, name }
  const [job, setJob]   = useState(null);   // id
  // Два взгляда на одно и то же. «По товару» отвечает на «с белым гладким
  // что-то не так», «по дням» — на «что было в среду»: за смену проходит
  // десяток позиций, и разбирают их днём целиком, а не поиском по названию.
  const [mode, setMode] = useState('product');   // product | day
  const [day, setDay]   = useState(null);        // 'YYYY-MM-DD'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ]       = useState('');
  const [qLive, setQLive] = useState('');
  const [code, setCode] = useState('');
  // «Сколько сделал ЭТОТ пресс» спрашивают чаще, чем «сколько оба»: по этому
  // считают загрузку и сравнивают смены. Раньше на вопрос отвечали глазами —
  // открывали день и складывали строки, помня, какая позиция где шла.
  const [presses, setPresses] = useState([]);
  const [pressIds, setPressIds] = useState([]);   // пусто — все

  useEffect(() => {
    productionAPI.getPresses()
      .then(r => setPresses(r.data || []))
      .catch(() => {});   // без списка просто не будет фильтра
  }, []);

  // Запрос уходит не на каждую букву: пока человек печатает «белый», список
  // успел бы перезагрузиться пять раз, и каждый раз — не тот.
  useEffect(() => {
    const tm = setTimeout(() => setQ(qLive), 400);
    return () => clearTimeout(tm);
  }, [qLive]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    // День едет с любым уровнем: провалившись в среду, дальше видим только
    // среду — и смены того же дня, а не всю историю декора.
    if (day) params.day = day;
    // Пресс едет со всеми уровнями сразу: выбрав его, внутрь дня и товара
    // проваливаемся уже с ним — иначе выбор терялся бы на первом же шаге.
    if (pressIds.length) params.press = pressIds.join(',');
    if (job) params.job = job;
    else if (nom) params.nomenclature = nom.id;
    else if (day) { /* день выбран — показываем его товары */ }
    else if (mode === 'day') params.by = 'day';
    else if (q.trim()) params.q = q.trim();
    productionAPI.getRegistry(params)
      .then(r => setData(r.data))
      .catch(() => toast.error(t('Ошибка загрузки')))
      .finally(() => setLoading(false));
  }, [nom, job, q, mode, day, pressIds]);

  useEffect(() => { load(); }, [load]);

  // Код с бирки: сканер или руками — попадаем сразу в ту смену, где лист сделан
  const findCode = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    try {
      const { data: r } = await productionAPI.getRegistry({ code: c });
      if (!r.found) { toast.error(t('Лист с таким кодом не найден')); return; }
      setDay(null);      // лист мог быть из другого дня — иначе не найдётся
      setNom(r.nomenclature_id ? { id: r.nomenclature_id, name: r.name } : null);
      setJob(r.job_id || null);
      if (!r.job_id) toast(t('Лист есть, но смена у него не указана'));
    } catch {
      toast.error(t('Ошибка поиска'));
    }
  };

  const inp = {
    padding: '9px 13px', background: 'var(--input)', border: '1px solid var(--border)',
    borderRadius: 9, color: 'var(--text)', fontSize: 14, outline: 'none',
    fontFamily: 'inherit',
  };

  const crumb = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
      <button onClick={() => { setNom(null); setJob(null); setDay(null); }} style={{
        background: 'none', border: 'none',
        color: (nom || day) ? 'var(--accent)' : 'var(--text)',
        fontSize: 15, fontWeight: 700, cursor: 'pointer', padding: 0, fontFamily: 'inherit',
      }}>📑 {t('Весь выпуск')}</button>
      {day && (
        <>
          <span style={{ color: 'var(--dim)' }}>›</span>
          <button onClick={() => { setNom(null); setJob(null); }} style={{
            background: 'none', border: 'none',
            color: nom ? 'var(--accent)' : 'var(--text)',
            fontSize: 15, fontWeight: 700, cursor: 'pointer', padding: 0, fontFamily: 'inherit',
          }}>{dt(day + 'T00:00:00')}</button>
        </>
      )}
      {nom && (
        <>
          <span style={{ color: 'var(--dim)' }}>›</span>
          <button onClick={() => setJob(null)} style={{
            background: 'none', border: 'none', color: job ? 'var(--accent)' : 'var(--text)',
            fontSize: 15, fontWeight: 700, cursor: 'pointer', padding: 0, fontFamily: 'inherit',
          }}>{nom.name || t('Продукт')}</button>
        </>
      )}
      {job && (
        <>
          <span style={{ color: 'var(--dim)' }}>›</span>
          <span style={{ fontSize: 15, fontWeight: 700 }}>{t('Смена')} #{job}</span>
        </>
      )}
    </div>
  );

  // ── Уровень 3: одна смена ────────────────────────────────────────────────
  const renderJob = () => {
    const j = data.job || {};
    // Где режим от листа к листу менялся — там он и нужен построчно.
    const varies = data.sheet_mode_varies || {};
    const TH = { padding: '6px 10px', fontWeight: 700, whiteSpace: 'nowrap' };
    const NUMTD = { padding: '8px 10px', color: 'var(--muted)',
                    whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
    const cell = (label, value) => (
      <div>
        <div style={{ fontSize: 11, color: 'var(--dim)', fontWeight: 700, textTransform: 'uppercase' }}>
          {t(label)}
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, marginTop: 3 }}>{value || '—'}</div>
      </div>
    );
    return (
      <>
        <div style={{ ...CARD, marginBottom: 16 }}>
          <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 3 }}>
            {j.product || j.texture || t('Смена')}
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 16 }}>
            {dt(j.started_at, true)}
            {j.completed_at && <> — {hhmm(j.completed_at)}</>}
            {j.shift_display && <> · {t(j.shift_display)}</>}
          </div>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14,
          }}>
            {cell('Пресс', j.press)}
            {cell('Оператор', j.operator)}
            {cell('Выпуск', `${j.quantity_done || 0} ${t('лист')}`)}
            {cell('Матрица', j.texture)}
          </div>
        </div>

        {/* Режим — то, ради чего реестр и нужен: по браку возвращаются сюда */}
        <div style={{ ...CARD, marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 14,
                        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            🌡 {t('Режим прессования')}
            {j.from_device > 0 ? (
              <span style={{
                background: '#0ea5e920', color: '#0ea5e9', borderRadius: 6,
                padding: '2px 9px', fontSize: 11.5, fontWeight: 700,
              }}>{t('со станка')}: {j.from_device} {t('лист')}</span>
            ) : (
              <span style={{
                background: 'var(--bg)', color: 'var(--dim)', borderRadius: 6,
                padding: '2px 9px', fontSize: 11.5, fontWeight: 600,
              }}>{t('записал оператор')}</span>
            )}
          </div>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14,
          }}>
            {cell('Температура плит', j.temperature)}
            {cell('Время ламинации, сек', j.hold_time_seconds)}
            {cell('Давление, МПа', j.pressure)}
            {cell('Температура в цехе', j.air_temp)}
            {cell('Влажность в цехе', j.humidity)}
            {cell('Партия ДСП', j.dsp_batch)}
            {cell('Паллета плёнки', j.film_pallet)}
          </div>
          {j.note && (
            <div style={{ marginTop: 14, color: 'var(--muted)', fontSize: 14 }}>{j.note}</div>
          )}
        </div>

        <div style={CARD}>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 12 }}>
            🏷 {t('Листы')} ({data.sheets?.length || 0})
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ color: 'var(--dim)', textAlign: 'left' }}>
                  <th style={TH}>{t('Код')}</th>
                  <th style={TH}>{t('Сорт')}</th>
                  <th style={TH}>{t('Время')}</th>
                  {/* Колонки режима показываем только там, где он от листа к
                      листу МЕНЯЛСЯ. Восемьдесят одинаковых цифр читать
                      незачем — общий режим стоит выше, в шапке смены. */}
                  {varies.temperature && <th style={TH}>{t('Темп. плит')}</th>}
                  {varies.hold_time_seconds && <th style={TH}>{t('Ламинация')}</th>}
                  {varies.pressure && <th style={TH}>{t('Давление')}</th>}
                  {varies.air_temp && <th style={TH}>{t('Цех, °C')}</th>}
                  {varies.humidity && <th style={TH}>{t('Влажн., %')}</th>}
                  <th style={TH}>{t('Состояние')}</th>
                </tr>
              </thead>
              <tbody>
                {(data.sheets || []).map(s => (
                  <tr key={s.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 700, fontFamily: 'monospace' }}>
                      {/* Тот же адрес, что в QR на бирке: паспорт листа уже есть */}
                      {s.code
                        ? <a href={`/s/${s.code}`} target="_blank" rel="noreferrer"
                             style={{ color: 'var(--accent)', textDecoration: 'none' }}>{s.code}</a>
                        : '—'}
                    </td>
                    <td style={{ padding: '8px 10px', color: s.is_default ? '#22c55e' : 'var(--muted)', fontWeight: 700 }}>
                      {t(s.grade)}
                    </td>
                    <td style={{ padding: '8px 10px', color: 'var(--muted)' }}>{hhmm(s.accepted_at)}</td>
                    {varies.temperature && <td style={NUMTD}>{s.temperature}</td>}
                    {varies.hold_time_seconds && <td style={NUMTD}>{s.hold_time_seconds}</td>}
                    {varies.pressure && <td style={NUMTD}>{s.pressure}</td>}
                    {varies.air_temp && <td style={NUMTD}>{s.air_temp}</td>}
                    {varies.humidity && <td style={NUMTD}>{s.humidity}</td>}
                    <td style={{ padding: '8px 10px' }}>
                      {s.is_shipped
                        ? <span style={{ color: 'var(--dim)' }}>{t('отгружен')} {dt(s.shipped_at)}</span>
                        : <span style={{ color: '#22c55e' }}>{t('на складе')}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  // ── Уровень 2: смены одного продукта ─────────────────────────────────────
  const renderJobs = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {(data.rows || []).map(r => (
        <button key={r.job_id || 'none'} onClick={() => r.job_id && setJob(r.job_id)}
          style={{
            ...CARD, textAlign: 'left', cursor: r.job_id ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
            color: 'var(--text)', fontFamily: 'inherit',
          }}>
          <div style={{ minWidth: 110 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>{dt(r.started_at)}</div>
            <div style={{ color: 'var(--dim)', fontSize: 13 }}>
              {hhmm(r.started_at)}{r.shift_display && ` · ${t(r.shift_display)}`}
            </div>
          </div>
          <div style={{ minWidth: 140 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{r.press || t('Смена не указана')}</div>
            <div style={{ color: 'var(--muted)', fontSize: 13 }}>{r.operator || '—'}</div>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <Grades items={r.grades} />
          </div>
          <div style={{ textAlign: 'right', minWidth: 110 }}>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{r.total}</div>
            <div style={{ color: 'var(--dim)', fontSize: 12 }}>
              {t('на складе')} {r.in_stock}
            </div>
          </div>
          {r.job_id && <span style={{ color: 'var(--dim)', fontSize: 20 }}>›</span>}
        </button>
      ))}
      {!loading && (data.rows || []).length === 0 && (
        <div style={{ ...CARD, color: 'var(--dim)', textAlign: 'center' }}>{t('Ничего не найдено')}</div>
      )}
    </div>
  );

  // ── Уровень 1: продукты ──────────────────────────────────────────────────
  const renderProducts = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {(data.rows || []).map(r => (
        <button key={r.nomenclature_id || 'none'}
          onClick={() => r.nomenclature_id && setNom({ id: r.nomenclature_id, name: r.short_name || r.name })}
          style={{
            ...CARD, textAlign: 'left', cursor: r.nomenclature_id ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
            color: 'var(--text)', fontFamily: 'inherit',
          }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>{r.short_name || r.name}</div>
            <div style={{ color: 'var(--dim)', fontSize: 12.5, marginTop: 2 }}>
              {t('последний выпуск')} {dt(r.last)}
            </div>
            <div style={{ marginTop: 8 }}><Grades items={r.grades} /></div>
          </div>
          <div style={{ textAlign: 'right', minWidth: 120 }}>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{r.total}</div>
            <div style={{ color: 'var(--dim)', fontSize: 12 }}>
              {t('на складе')} {r.in_stock} · {t('отгружено')} {r.shipped}
            </div>
          </div>
          <span style={{ color: 'var(--dim)', fontSize: 20 }}>›</span>
        </button>
      ))}
      {!loading && (data.rows || []).length === 0 && (
        <div style={{ ...CARD, color: 'var(--dim)', textAlign: 'center' }}>{t('Ничего не найдено')}</div>
      )}
    </div>
  );

  // Дни: сверху свежие. В строке — то, по чему день узнают: сколько листов,
  // сколько позиций, какие прессы стояли.
  const renderDays = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {(data.rows || []).map(r => (
        <button key={r.day} onClick={() => setDay(r.day)} style={{
          ...CARD, textAlign: 'left', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
          color: 'var(--text)', fontFamily: 'inherit',
        }}>
          <span style={{ minWidth: 108 }}>
            <span style={{ display: 'block', fontSize: 17, fontWeight: 800 }}>
              {dt(r.day + 'T00:00:00')}
            </span>
            <span style={{ display: 'block', color: 'var(--dim)', fontSize: 12.5 }}>
              {new Date(r.day + 'T00:00:00').toLocaleDateString('ru-RU', { weekday: 'long' })}
            </span>
          </span>

          <span style={{ flex: '1 1 220px', minWidth: 0 }}>
            <span style={{ display: 'block', color: 'var(--muted)', fontSize: 13.5,
                           marginBottom: 6 }}>
              {r.products} {t('позиций')} · {r.jobs} {t('смен')}
              {r.presses.length > 0 && <> · {r.presses.join(', ')}</>}
            </span>
            <Grades items={r.grades} />
          </span>

          <span style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
            <span style={{ display: 'block', fontSize: 22, fontWeight: 800 }}>{r.total}</span>
            <span style={{ display: 'block', color: 'var(--dim)', fontSize: 12.5 }}>
              {t('на складе')} {r.in_stock}
              {r.shipped > 0 && <> · {t('отгружено')} {r.shipped}</>}
            </span>
          </span>
          <span style={{ color: 'var(--dim)' }}>›</span>
        </button>
      ))}
      {!loading && (data.rows || []).length === 0 && (
        <div style={{ ...CARD, color: 'var(--dim)', textAlign: 'center' }}>
          {t('За этот период выпуска не было')}
        </div>
      )}
    </div>
  );

  return (
    <Shell title={t('Что выпустили')}>
      {crumb}

      {/* Переключатель показываем только наверху: внутри дня или товара он
          сбивал бы с толку — там уже выбран угол, под которым смотрят. */}
      {!job && !nom && !day && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          {[['product', '📦 ' + t('По товару')], ['day', '📅 ' + t('По дням')]]
            .map(([k, label]) => (
              <button key={k} onClick={() => setMode(k)} style={{
                background: mode === k ? 'var(--accent)' : 'var(--card)',
                color: mode === k ? '#fff' : 'var(--muted)',
                border: '1px solid ' + (mode === k ? 'var(--accent)' : 'var(--border)'),
                borderRadius: 10, padding: '9px 18px', fontSize: 14, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
              }}>{label}</button>
            ))}
        </div>
      )}

      {/* Прессы. Показываем, только когда их правда несколько: одна кнопка
          «все» рядом с одним прессом — это забытая настройка, а не фильтр.
          Внутри одной смены фильтр не нужен — пресс у неё уже один. */}
      {!job && presses.length > 1 && (
        <div style={{
          display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap',
          alignItems: 'center',
        }}>
          <span style={{ color: 'var(--dim)', fontSize: 13, marginRight: 2 }}>
            {t('Пресс')}:
          </span>
          {[{ id: 0, name: t('Все') }, ...presses].map(p => {
            const on = p.id === 0 ? pressIds.length === 0 : pressIds.includes(p.id);
            return (
              <button key={p.id} onClick={() => setPressIds(
                p.id === 0 ? []
                  : pressIds.includes(p.id) ? pressIds.filter(x => x !== p.id)
                  : [...pressIds, p.id])}
                style={{
                  background: on ? 'var(--accent)' : 'var(--card)',
                  color: on ? '#fff' : 'var(--muted)',
                  border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'),
                  borderRadius: 10, padding: '7px 14px', fontSize: 13.5,
                  fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                }}>{p.name}</button>
            );
          })}
        </div>
      )}

      {!job && (
        <div style={{ ...CARD, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {!nom && mode === 'product' && !day && (
            <input value={qLive} onChange={e => setQLive(e.target.value)}
              placeholder={t('Поиск по названию ЛДСП')}
              style={{ ...inp, flex: 1, minWidth: 220 }} />
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={code} onChange={e => setCode(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && findCode()}
              placeholder={t('Код с бирки')}
              style={{ ...inp, width: 150, fontFamily: 'monospace', textTransform: 'uppercase' }} />
            <button onClick={findCode} style={{ ...inp, cursor: 'pointer', fontWeight: 700 }}>
              🔍 {t('Найти лист')}
            </button>
          </div>
        </div>
      )}

      {loading && !data && (
        <div style={{ ...CARD, color: 'var(--dim)', textAlign: 'center' }}>{t('Загрузка')}…</div>
      )}

      {data && data.level === 'days' && renderDays()}
      {data && data.level === 'products' && renderProducts()}
      {data && data.level === 'jobs' && renderJobs()}
      {data && data.level === 'job' && renderJob()}
    </Shell>
  );
}
